import React, { useState, useEffect, useRef } from 'react';
import { useAPR } from '../context/APRContext';
import { useAuth } from '../context/AuthContext';
import apiService, { getApiBaseUrl } from '../services/api';
import axios from 'axios';
import '../styles/Attributes.css';

// Use centralized API config for environment-aware URL resolution
const API_BASE_URL = getApiBaseUrl();

interface Framework {
  id: string;
  name: string;
  code?: string;
  description?: string;
  type?: string;
  status?: string;
  default_for_application_type?: string;
  created_at?: string;
  updated_at?: string;
  dimension_count?: number;
  component_count?: number;
  attribute_count?: number;
}

interface AttributeWithDetails {
  id: string;
  name: string;
  description?: string;
  weight: number;
  displayOrder: number;
  componentId: string;
  componentName: string;
  dimensionId: string;
  dimensionName: string;
  question?: {
    id: string;
    attributeId: string;
    text: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface ComponentWithDetails {
  id: string;
  name: string;
  description?: string;
  displayOrder: number;
  dimensionId: string;
  dimensionName: string;
  attributes: AttributeWithDetails[];
}

interface DimensionWithDetails {
  id: string;
  name: string;
  description?: string;
  displayOrder: number;
  components: ComponentWithDetails[];
  createdAt: string;
  updatedAt: string;
}

const Attributes: React.FC = () => {
  const { dispatch } = useAPR();
  const { hasPermission, currentUser } = useAuth();

  // State management
  const [attributes, setAttributes] = useState<AttributeWithDetails[]>([]);
  const [components, setComponents] = useState<ComponentWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(null);
  const [editingAttribute, setEditingAttribute] = useState<AttributeWithDetails | null>(null);

  const [viewMode, setViewMode] = useState<'hierarchy' | 'table'>('hierarchy');
  const [showEditForm, setShowEditForm] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    weight: 3,
    componentId: ''
  });

  // File upload state
  const [uploadedDimensions, setUploadedDimensions] = useState<any[]>([]);
  const [showUploadPreview, setShowUploadPreview] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Delete all state
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteStatus, setDeleteStatus] = useState<string | null>(null);

  // Ref for attributes column to reset scroll on component change
  const attributesColumnRef = useRef<HTMLDivElement>(null);

  // Reset attributes panel scroll when selected component changes
  useEffect(() => {
    if (attributesColumnRef.current) {
      attributesColumnRef.current.scrollTop = 0;
    }
  }, [selectedComponent]);

  const canModify = hasPermission('canManageSettings');
  const isAdmin = currentUser?.role === 'Administrator';

  // Load components and attributes directly
  const loadComponentsAndAttributes = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');

      // Load all components with their attributes
      const response = await axios.get(`${API_BASE_URL}/components`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const componentsData = response.data || [];

      // Transform to ComponentWithDetails format
      const transformedComponents: ComponentWithDetails[] = componentsData.map((comp: any) => ({
        id: comp.id,
        name: comp.name,
        description: comp.description,
        displayOrder: comp.displayOrder || 0,
        dimensionId: comp.dimensionId || '',
        dimensionName: comp.dimensionName || '',
        attributes: (comp.attributes || []).map((attr: any) => ({
          id: attr.id,
          name: attr.name,
          description: attr.description,
          weight: attr.weight || 3,
          displayOrder: attr.displayOrder || 0,
          componentId: comp.id,
          componentName: comp.name,
          dimensionId: comp.dimensionId || '',
          dimensionName: comp.dimensionName || '',
          createdAt: attr.createdAt,
          updatedAt: attr.updatedAt
        }))
      }));

      // Extract all attributes
      const attributesData: AttributeWithDetails[] = transformedComponents.flatMap(comp => comp.attributes);

      setComponents(transformedComponents);
      setAttributes(attributesData);
      setError(null);
    } catch (err: any) {
      console.error('Error loading components:', err);
      setError('Failed to load components. Please refresh the page.');
      setAttributes([]);
      setComponents([]);
    } finally {
      setLoading(false);
    }
  };

  // Load components on mount
  useEffect(() => {
    loadComponentsAndAttributes();
  }, []);

  // Event handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: name === 'weight' ? parseInt(value) || 1 : value 
    }));
  };

  const handleEditAttribute = (attribute: AttributeWithDetails) => {
    setEditingAttribute(attribute);
    setFormData({
      name: attribute.name,
      description: attribute.description || '',
      weight: attribute.weight,
      componentId: attribute.componentId
    });
    setShowEditForm(true);
  };

  const handleCreateAttribute = () => {
    setEditingAttribute(null);
    setFormData({
      name: '',
      description: '',
      weight: 3,
      componentId: selectedComponent || ''
    });
    setShowCreateForm(true);
  };

  const handleDeleteAttribute = async (attribute: AttributeWithDetails) => {
    if (!canModify) return;
    
    const confirmed = window.confirm(`Are you sure you want to delete attribute "${attribute.name}"? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      await apiService.deleteAttribute(attribute.id);
      
      // Update local state
      setAttributes(prev => prev.filter(attr => attr.id !== attribute.id));
      
      // Update components state
      setComponents(prev => prev.map(comp => ({
        ...comp,
        attributes: comp.attributes.filter(attr => attr.id !== attribute.id)
      })));
    } catch (err: any) {
      alert('Failed to delete attribute. Please try again.');
    }
  };

  const handleSaveAttribute = async () => {
    if (!formData.name.trim() || !formData.componentId) return;

    // Check for duplicate name (case-insensitive)
    const trimmedName = formData.name.trim().toLowerCase();
    const duplicateAttribute = attributes.find(a =>
      a.name.toLowerCase() === trimmedName &&
      (!editingAttribute || a.id !== editingAttribute.id)
    );

    if (duplicateAttribute) {
      alert(`An attribute with the name "${formData.name.trim()}" already exists. Please use a different name.`);
      return;
    }

    try {
      if (editingAttribute) {
        // Update existing attribute
        const updatedAttribute = await apiService.updateAttribute(editingAttribute.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          weight: formData.weight,
          componentId: formData.componentId
        });
        
        // Update local state
        setAttributes(prev => prev.map(attr => 
          attr.id === editingAttribute.id ? updatedAttribute : attr
        ));
        
        // If component changed, update all data structures
        if (editingAttribute.componentId !== formData.componentId) {
          // Remove from old component and add to new component
          setComponents(prev => prev.map(comp => {
            if (comp.id === editingAttribute.componentId) {
              // Remove from old component
              return { ...comp, attributes: comp.attributes.filter(attr => attr.id !== editingAttribute.id) };
            } else if (comp.id === formData.componentId) {
              // Add to new component
              return { ...comp, attributes: [...comp.attributes, updatedAttribute] };
            }
            return comp;
          }));
        } else {
          // Just update the attribute in place
          setComponents(prev => prev.map(comp =>
            comp.id === formData.componentId
              ? { ...comp, attributes: comp.attributes.map(attr => attr.id === editingAttribute.id ? updatedAttribute : attr) }
              : comp
          ));
        }
      } else {
        // Create new attribute
        const newAttribute = await apiService.createAttribute({
          name: formData.name.trim(),
          description: formData.description.trim(),
          weight: formData.weight,
          componentId: formData.componentId
        });
        
        // Update local state - add new attribute
        setAttributes(prev => [...prev, newAttribute]);
        
        // Update components state - add attribute to the correct component
        setComponents(prev => prev.map(comp =>
          comp.id === formData.componentId
            ? { ...comp, attributes: [...comp.attributes, newAttribute] }
            : comp
        ));
      }
      
      handleCancelEdit();
    } catch (err: any) {
      alert('Failed to save attribute. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setShowCreateForm(false);
    setEditingAttribute(null);
    setFormData({ name: '', description: '', weight: 3, componentId: '' });
  };

  // Attribute bulk upload functions
  const downloadDimensionsTemplate = () => {
    const headers = ['Component', 'Attribute', 'Weightage Score', 'Description'];

    const csvContent = headers.join(',') + '\n' +
      'Alignment to Global Standards,Compliance with industry frameworks,4,Measures how well the application adheres to required industry regulatory and compliance frameworks.\n' +
      'Alignment to Global Standards,Adoption of enterprise standards,4,Evaluates use of approved enterprise standards technologies and governance models.\n' +
      'Alignment to Strategy,Strategic relevance,4,Measures how directly the application contributes to strategic business priorities.\n' +
      'Infrastructure Costs,Hosting model (on-prem/cloud),4,Identifies hosting method and cost implications across on-prem cloud or hybrid models.\n' +
      'Operational Costs,Support team size,4,Assesses staffing required to support maintain and operate the application.';

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attributes_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadAttributesData = () => {
    if (attributes.length === 0) {
      alert('No attributes data to download');
      return;
    }

    const headers = ['Component', 'Attribute', 'Weightage Score', 'Description'];
    const csvRows = [headers.join(',')];

    attributes.forEach(attr => {
      const row = [
        `"${(attr.componentName || '').replace(/"/g, '""')}"`,
        `"${(attr.name || '').replace(/"/g, '""')}"`,
        attr.weight || 3,
        `"${(attr.description || '').replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attributes_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let text = e.target?.result as string;

        // Handle BOM if present
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.slice(1);
        }

        // Normalize line endings
        text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

        // Parse CSV
        const parseCSVLine = (line: string): string[] => {
          const result: string[] = [];
          let current = '';
          let inQuotes = false;

          for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"' || char === "'") {
              inQuotes = !inQuotes;
            } else if ((char === ',' || char === ';' || char === '\t') && !inQuotes) {
              result.push(current.trim());
              current = '';
            } else if (char !== '\r') {
              current += char;
            }
          }

          result.push(current.trim());
          return result;
        };

        const lines = text.split('\n').filter(line => line.trim());

        if (lines.length < 2) {
          alert('File appears to be empty or contains only headers.');
          return;
        }

        const headers = parseCSVLine(lines[0]).map(h =>
          h.replace(/['"]/g, '').trim()
        );

        const data = lines.slice(1).map(line => {
          const values = parseCSVLine(line).map(v =>
            v.replace(/['"]/g, '').trim()
          );
          const obj: any = {};

          headers.forEach((header, index) => {
            obj[header] = values[index] || '';
          });

          return obj;
        }).filter(row => {
          const hasData = Object.values(row).some(value =>
            value && value.toString().trim() !== ''
          );
          return hasData;
        });

        if (data.length === 0) {
          alert('No valid data rows found in the file.');
          return;
        }

        setUploadedDimensions(data);
        setShowUploadPreview(true);
      } catch (error) {
        console.error('Error parsing file:', error);
        alert(`Error parsing file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    reader.readAsText(file, 'UTF-8');
  };

  const processBulkDimensionUpload = async () => {
    try {
      const attributesToCreate = uploadedDimensions.map(row => {
        const getFieldValue = (possibleHeaders: string[]): string => {
          for (const header of possibleHeaders) {
            if (row[header] !== undefined && row[header] !== null) {
              return String(row[header]).trim();
            }
          }
          return '';
        };

        return {
          component: getFieldValue(['Component', 'component']),
          attribute: getFieldValue(['Attribute', 'attribute', 'Attribute Name', 'name']),
          weightage: parseInt(getFieldValue(['Weightage Score', 'Weightage', 'Weight', 'weightage', 'weight']) || '3'),
          description: getFieldValue(['Description', 'description'])
        };
      });

      // Filter out invalid entries and check for duplicates
      const validAttributes = [];
      const errors = [];
      const skipped = [];

      for (const attr of attributesToCreate) {
        // Validate required fields
        if (!attr.component || !attr.attribute) {
          errors.push(`Missing required fields for attribute: ${attr.attribute || 'Unknown'}`);
          continue;
        }

        // Find component by name - strip all non-alphanumeric for robust matching
        const normalizeForMatch = (str: string) => str
          .replace(/[^a-zA-Z0-9\s]/g, '')  // Remove ALL non-alphanumeric (except spaces)
          .replace(/\s+/g, ' ')  // Normalize whitespace
          .trim()
          .toLowerCase();
        console.log('🔍 Component matching:', { csvComponent: attr.component, normalized: normalizeForMatch(attr.component) });
        console.log('🔍 Available components:', components.map(c => ({ name: c.name, normalized: normalizeForMatch(c.name) })));
        const component = components.find(c =>
          normalizeForMatch(c.name) === normalizeForMatch(attr.component)
        );

        if (!component) {
          const availableComponents = components.map(c => c.name).join(', ');
          errors.push(`Component "${attr.component}" not found. Available components: ${availableComponents || 'None'}`);
          continue;
        }

        // Check for duplicate attribute in this component
        const existingAttribute = attributes.find(a =>
          a.name.toLowerCase() === attr.attribute.toLowerCase() &&
          a.componentId === component.id
        );
        if (existingAttribute) {
          skipped.push(`${attr.attribute} (already exists in ${attr.component})`);
          continue;
        }

        // Validate weightage (1-5)
        if (attr.weightage < 1 || attr.weightage > 5) {
          attr.weightage = 3; // Default to moderate if invalid
        }

        validAttributes.push({
          name: attr.attribute,
          description: attr.description,
          weight: attr.weightage,
          componentId: component.id
        });
      }

      // Show errors and skipped items
      if (errors.length > 0) {
        alert(`Errors found:\n${errors.join('\n')}`);
      }
      if (skipped.length > 0) {
        alert(`The following attributes already exist and will be skipped:\n${skipped.join('\n')}`);
      }

      if (validAttributes.length === 0) {
        alert('No valid attributes to create.');
        setShowUploadPreview(false);
        setUploadedDimensions([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Create attributes one by one
      const createdAttributes = [];
      for (const attrData of validAttributes) {
        try {
          const newAttr = await apiService.createAttribute(attrData);
          createdAttributes.push(newAttr);
        } catch (error) {
          console.error(`Failed to create attribute ${attrData.name}:`, error);
        }
      }

      if (createdAttributes.length > 0) {
        // Reload all data
        await loadComponentsAndAttributes();

        alert(`Successfully created ${createdAttributes.length} attribute(s).`);
      }

      // Clear upload state
      setShowUploadPreview(false);
      setUploadedDimensions([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error creating attributes:', error);
      alert(`Error creating attributes: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const cancelBulkUpload = () => {
    setShowUploadPreview(false);
    setUploadedDimensions([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteAllData = async () => {
    if (!isAdmin) {
      setDeleteStatus('Only administrators can delete all attributes');
      return;
    }

    if (deleteConfirmText !== 'DELETE') {
      alert('Please type "DELETE" to confirm deletion');
      return;
    }

    setLoading(true);
    setDeleteStatus('Deleting all attributes...');

    try {
      let deletedCount = 0;

      // Delete all attributes
      for (const attr of attributes) {
        try {
          await apiService.deleteAttribute(attr.id);
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete attribute ${attr.id}:`, error);
        }
      }

      // Reload data
      await loadComponentsAndAttributes();

      setDeleteStatus(`Successfully deleted ${deletedCount} attributes`);
      setShowDeleteAllModal(false);
      setDeleteConfirmText('');
    } catch (error) {
      console.error('Error deleting all attributes:', error);
      setDeleteStatus('Failed to delete all attributes. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeleteAll = () => {
    setShowDeleteAllModal(false);
    setDeleteConfirmText('');
    setDeleteStatus(null);
  };

  // Utility functions
  const getDimensionIcon = (dimensionName: string) => {
    const icons: Record<string, string> = {
      'Business Value and Strategic Fit': '🎯',
      'Business Case': '💰',
      'Application Performance': '⚡',
      'Application Risk Mitigation': '🛡️',
      'Architectural Maturity': '🏗️'
    };
    return icons[dimensionName] || '📋';
  };

  const getWeightStyle = (weight: number) => {
    const styles = {
      1: { background: '#ef5350', color: '#ffffff' },  // Bright Red
      2: { background: '#ff9800', color: '#ffffff' },  // Bright Orange
      3: { background: '#fdd835', color: '#000000' },  // Bright Yellow
      4: { background: '#66bb6a', color: '#ffffff' },  // Bright Green
      5: { background: '#2e7d32', color: '#ffffff' }   // Dark Green
    };
    return styles[weight as keyof typeof styles] || { background: '#f5f5f5', color: '#666' };
  };

  const getWeightLabel = (weight: number) => {
    const labels = {
      1: 'Least Important',
      2: 'Low Importance',
      3: 'Moderate Importance',
      4: 'High Importance', 
      5: 'Critical'
    };
    return labels[weight as keyof typeof labels] || 'Unknown';
  };

  // Computed values
  const selectedComponentData = selectedComponent && components
    ? components.find(c => c.id === selectedComponent)
    : null;

  const weightDistribution = attributes.reduce((acc, attr) => {
    acc[attr.weight] = (acc[attr.weight] || 0) + 1;
    return acc;
  }, {} as Record<number, number>);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Data Attributes and Weightage</h1>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⏳</div>
            <h3>Loading Attributes</h3>
            <p>Please wait while we fetch framework attributes and weightings...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Data Attributes and Weightage</h1>
            </div>
          </div>
        </div>
        <div className="master-data-container">
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <div className="empty-state-actions">
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Data Attributes and Weightage</h1>
          </div>
          <div className="page-header__actions">
            {canModify && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateAttribute}
                  disabled={!selectedComponent}
                  title={!selectedComponent ? "Please select a component first" : "Add new attribute"}
                >
                  ➕ Add Attribute
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={downloadDimensionsTemplate}
                  title="Download CSV template for bulk attribute upload"
                >
                  📋 Download Template
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload CSV file with attributes"
                >
                  📤 Upload Attributes
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".csv"
                  style={{ display: 'none' }}
                />
              </>
            )}
            {isAdmin && attributes.length > 0 && (
              <button
                className="btn btn-danger"
                onClick={() => setShowDeleteAllModal(true)}
                disabled={loading}
              >
                🗑️ Delete All Data
              </button>
            )}
            {attributes.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadAttributesData}
                title="Download current attributes data as CSV"
                style={{ border: '2px solid #28a745' }}
              >
                📥 Download Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="form-modal">
          <div className="form-modal-content" style={{ maxWidth: '500px' }}>
            <div className="form-modal-header" style={{ backgroundColor: '#dc3545' }}>
              <h2>⚠️ Confirm Delete All Attributes</h2>
              <button
                className="btn-close"
                onClick={handleCancelDeleteAll}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '6px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <p style={{ margin: 0, color: '#856404', fontWeight: '600', fontSize: '14px' }}>
                  ⚠️ WARNING: This action cannot be undone!
                </p>
                <p style={{ margin: '8px 0 0 0', color: '#856404', fontSize: '13px' }}>
                  This will permanently delete all {attributes.length} attributes from the system.
                </p>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>
                  Type <strong style={{ color: '#dc3545' }}>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #dc3545',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace'
                  }}
                  autoFocus
                />
              </div>

              <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelDeleteAll}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteAllData}
                  disabled={loading || deleteConfirmText !== 'DELETE'}
                  style={{
                    opacity: deleteConfirmText !== 'DELETE' ? 0.5 : 1,
                    cursor: deleteConfirmText !== 'DELETE' ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loading ? 'Deleting...' : '🗑️ Delete All Attributes'}
                </button>
              </div>

              {deleteStatus && (
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  borderRadius: '4px',
                  backgroundColor: deleteStatus.includes('Failed') || deleteStatus.includes('error') ? '#f8d7da' : '#d4edda',
                  color: deleteStatus.includes('Failed') || deleteStatus.includes('error') ? '#721c24' : '#155724',
                  fontSize: '14px'
                }}>
                  {deleteStatus}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Container */}
      <div className="master-data-container">

      {/* Upload Preview Modal */}
      {showUploadPreview && (
        <div className="modal-overlay" onClick={cancelBulkUpload}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '600px',
              maxWidth: '800px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '500', color: '#1a1a1a' }}>
                Preview Attribute Upload
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                Review the attributes to be created. Duplicates and invalid entries will be skipped.
              </p>
            </div>

            <div style={{ marginBottom: '20px', maxHeight: '400px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Component</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Attribute</th>
                    <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600' }}>Weight</th>
                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: '600' }}>Description</th>
                    <th style={{ padding: '10px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedDimensions.map((row, index) => {
                    const componentName = row['Component'] || row['component'] || '';
                    const attributeName = row['Attribute'] || row['attribute'] || '';
                    const weightage = row['Weightage Score'] || row['Weightage'] || row['Weight'] || '3';
                    const description = row['Description'] || row['description'] || '';

                    // Check validity - strip all non-alphanumeric for robust matching
                    const normalizeForMatch = (str: string) => str
                      .replace(/[^a-zA-Z0-9\s]/g, '')  // Remove ALL non-alphanumeric (except spaces)
                      .replace(/\s+/g, ' ')  // Normalize whitespace
                      .trim()
                      .toLowerCase();
                    const component = components.find(c =>
                      normalizeForMatch(c.name) === normalizeForMatch(componentName)
                    );
                    const isDuplicate = component ? attributes.some(a =>
                      a.name.toLowerCase() === attributeName.toLowerCase() &&
                      a.componentId === component.id
                    ) : false;

                    let status = 'Valid';
                    let bgColor = 'white';
                    if (!componentName || !attributeName) {
                      status = 'Missing fields';
                      bgColor = '#f8d7da';
                    } else if (!component) {
                      status = 'Component not found';
                      bgColor = '#f8d7da';
                    } else if (isDuplicate) {
                      status = 'Duplicate';
                      bgColor = '#fff3cd';
                    }

                    return (
                      <tr
                        key={index}
                        style={{
                          borderBottom: '1px solid #eee',
                          backgroundColor: bgColor
                        }}
                      >
                        <td style={{ padding: '10px' }}>{componentName}</td>
                        <td style={{ padding: '10px' }}>{attributeName}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>{weightage}</td>
                        <td style={{ padding: '10px', maxWidth: '250px', fontSize: '12px' }}>{description}</td>
                        <td style={{ padding: '10px', textAlign: 'center', fontSize: '12px', fontWeight: '500' }}>
                          <span style={{
                            color: status === 'Valid' ? '#28a745' : (status === 'Duplicate' ? '#856404' : '#721c24')
                          }}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={cancelBulkUpload}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  backgroundColor: 'transparent',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={processBulkDimensionUpload}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'white',
                  backgroundColor: '#1976d2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Create Attributes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/Create Form Modal */}
      {(showEditForm || showCreateForm) && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '500px',
              maxWidth: '600px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 8px 24px rgba(0,0,0,0.15)',
              position: 'relative'
            }}
          >
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '500',
                color: '#1a1a1a'
              }}>
                {editingAttribute ? 'Edit Attribute' : 'Create New Attribute'}
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#666'
              }}>
                {editingAttribute
                  ? 'Update attribute information and weight'
                  : 'Add a new attribute to the APR framework'
                }
              </p>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveAttribute(); }}>
              {!editingAttribute && (
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="componentId" style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Component *
                  </label>
                  <select
                    id="componentId"
                    name="componentId"
                    value={formData.componentId}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '14px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Select a component</option>
                    {components?.map(component => (
                      <option key={component.id} value={component.id}>
                        {component.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="name" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Attribute Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter attribute name"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="description" style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333'
                }}>
                  Attribute Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  placeholder="Describe what this attribute evaluates and its purpose in the assessment framework"
                />
                <small style={{
                  display: 'block',
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  This description helps users understand what aspects this attribute covers during application assessment.
                </small>
              </div>

              <div className="md-weight-selector">
                <h3 className="md-weight-selector__title">Attribute Weight</h3>
                <div className="md-weight-selector__options">
                  {[1, 2, 3, 4, 5].map(weightValue => (
                    <label key={weightValue} className="md-weight-option">
                      <input
                        type="radio"
                        name="weight"
                        value={weightValue}
                        checked={formData.weight === weightValue}
                        onChange={handleInputChange}
                        className="md-weight-option__input"
                      />
                      <div
                        className="md-weight-option__button"
                        style={{ ...getWeightStyle(weightValue) }}
                      >
                        {weightValue}
                      </div>
                      <span className="md-weight-option__label">{getWeightLabel(weightValue)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    backgroundColor: 'transparent',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'white',
                    backgroundColor: '#1976d2',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                >
                  {editingAttribute ? 'Save Attribute' : 'Create Attribute'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="md-content">
        {viewMode === 'table' ? (
          /* Table View */
          <div className="md-table-container">
            <table className="md-table" style={{ fontSize: '12px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'center', padding: '6px', fontSize: '11px', whiteSpace: 'nowrap' }}>Component</th>
                  <th style={{ textAlign: 'center', padding: '6px', fontSize: '11px', whiteSpace: 'nowrap' }}>Attribute</th>
                  <th style={{ textAlign: 'center', padding: '6px', fontSize: '11px', whiteSpace: 'nowrap' }}>Weight</th>
                  <th style={{ textAlign: 'center', padding: '6px', fontSize: '11px', whiteSpace: 'nowrap' }}>Description</th>
                  {canModify && <th style={{ textAlign: 'center', padding: '6px', fontSize: '11px', whiteSpace: 'nowrap' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {attributes.map((attribute) => (
                  <tr key={attribute.id}>
                    <td style={{ textAlign: 'left', padding: '6px', fontSize: '12px' }}>
                      {attribute.componentName}
                    </td>
                    <td style={{ textAlign: 'left', padding: '6px', fontSize: '12px' }}>
                      <strong>{attribute.name}</strong>
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px', fontSize: '12px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          ...getWeightStyle(attribute.weight),
                          fontSize: '12px',
                          fontWeight: '600',
                          textAlign: 'center',
                          minWidth: '24px'
                        }}
                      >
                        {attribute.weight}
                      </span>
                    </td>
                    <td style={{ textAlign: 'left', padding: '6px', fontSize: '12px', maxWidth: '300px' }}>
                      {attribute.description || 'No description'}
                    </td>
                    {canModify && (
                      <td style={{ textAlign: 'center', padding: '6px', fontSize: '12px' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button 
                            onClick={() => handleEditAttribute(attribute)}
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#1976d2',
                              border: '1px solid #1976d2',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                            title="Edit attribute"
                          >
                            ✏️
                          </button>
                          <button 
                            onClick={() => handleDeleteAttribute(attribute)}
                            style={{
                              padding: '4px 6px',
                              backgroundColor: 'transparent',
                              color: '#d32f2f',
                              border: '1px solid #d32f2f',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: 'pointer'
                            }}
                            title="Delete attribute"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {attributes.length === 0 && !loading && !error && (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h3>No Attributes Found</h3>
                <p>Get started by uploading attributes using the CSV template, or create them individually.</p>
                {canModify && (
                  <div className="empty-state-actions">
                    <button
                      className="btn btn-outline-info"
                      onClick={downloadDimensionsTemplate}
                      style={{ marginRight: '12px' }}
                    >
                      Download Template
                    </button>
                    <button className="btn btn-primary" onClick={handleCreateAttribute}>
                      Create Attribute
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Hierarchy View */
          <div className="attributes-layout">
          {/* Components Column - scrolls independently */}
          <div className="components-column">
            <div className="md-navigation-rail__header">
              <h3 className="md-navigation-rail__title">Components & Attributes</h3>
            </div>

            {/* Component Selection */}
            <div className="md-component-selector">
              <h4 className="md-component-selector__title">Select Component</h4>
              <div className="md-component-list">
                {components?.map(component => (
                  <button
                    key={component.id}
                    className={`md-navigation-item ${selectedComponent === component.id ? 'md-navigation-item--selected' : ''}`}
                    onClick={() => setSelectedComponent(component.id)}
                  >
                    <span className="md-navigation-item__label">{component.name}</span>
                    <span className="md-navigation-item__badge">{component.attributes?.length || 0}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Attribute Weight Distribution Chart */}
            <div className="md-weight-distribution">
              <h4 className="md-weight-distribution__title">Attribute Weight Distribution</h4>
              <div className="md-weight-distribution__chart">
                {[1, 2, 3, 4, 5].map(weight => (
                  <div key={weight} className="md-weight-bar">
                    <div className="md-weight-bar__info">
                      <span
                        className="md-weight-bar__dot"
                        style={{ backgroundColor: getWeightStyle(weight).background }}
                      ></span>
                      <span className="md-weight-bar__label">{weight}</span>
                    </div>
                    <div className="md-weight-bar__track">
                      <div
                        className="md-weight-bar__fill"
                        style={{
                          width: attributes.length > 0 ? `${((weightDistribution[weight] || 0) / attributes.length) * 100}%` : '0%',
                          backgroundColor: getWeightStyle(weight).background
                        }}
                      ></div>
                    </div>
                    <span className="md-weight-bar__count">{weightDistribution[weight] || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Attributes Column - stays at top */}
          <div ref={attributesColumnRef} className="attributes-column">
            {selectedComponentData ? (
              <div className="md-component-attributes">
                <div className="md-component-header">
                  <div className="md-component-header__text">
                    <h2 className="md-component-header__title">{selectedComponentData.name}</h2>
                    <p className="md-component-header__subtitle">
                      Component Attributes
                    </p>
                  </div>
                  <div className="md-component-header__stats">
                    <div className="md-chip md-chip--filled">
                      <span className="md-chip__text">{selectedComponentData.attributes?.length || 0} Attributes</span>
                    </div>
                    <div className="md-chip md-chip--outlined">
                      <span className="md-chip__text">
                        Avg Attribute Weight: {selectedComponentData.attributes?.length > 0 
                          ? (selectedComponentData.attributes.reduce((sum, attr) => sum + attr.weight, 0) / selectedComponentData.attributes.length).toFixed(1)
                          : '0'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="md-attributes-grid">
                  {selectedComponentData.attributes?.map(attribute => (
                    <div key={attribute.id} className="md-card md-card--outlined md-attribute-card">
                      <div className="md-card__content">
                        <div className="md-attribute-card__header">
                          <h3 className="md-attribute-card__title">{attribute.name}</h3>
                          {canModify && (
                            <div className="md-attribute-card__actions">
                              <button 
                                className="md-icon-button md-icon-button--standard"
                                onClick={() => handleEditAttribute(attribute)}
                                title="Edit attribute"
                              >
                                ✏️
                              </button>
                              <button 
                                className="md-icon-button md-icon-button--standard"
                                onClick={() => handleDeleteAttribute(attribute)}
                                title="Delete attribute"
                              >
                                🗑️
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div className="md-attribute-card__weight">
                          <div
                            className="md-weight-badge"
                            style={{
                              ...getWeightStyle(attribute.weight),
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              display: 'inline-block',
                              minWidth: '24px',
                              textAlign: 'center'
                            }}
                          >
                            {attribute.weight}
                          </div>
                          <span className="md-weight-badge__label">
                            {getWeightLabel(attribute.weight)}
                          </span>
                        </div>

                        {attribute.description && (
                          <p className="md-attribute-card__description">{attribute.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="md-empty-state">
                <div className="md-empty-state__icon">⚖️</div>
                <h3 className="md-empty-state__title">Select a Component</h3>
                <p className="md-empty-state__description">
                  Choose a component from the left panel to view and configure attribute weights.
                </p>
                <div className="md-weight-legend">
                  <h4 className="md-weight-legend__title">Attribute Weight Scale:</h4>
                  <div className="md-weight-legend__items">
                    {[1, 2, 3, 4, 5].map(weight => (
                      <div key={weight} className="md-weight-legend__item">
                        <span
                          className="md-weight-legend__dot"
                          style={{ backgroundColor: getWeightStyle(weight).background }}
                        ></span>
                        <span className="md-weight-legend__label">{weight} - {getWeightLabel(weight)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
          )}
      </div>
      </div>
    </div>
  );
};

export default Attributes;